import {
  Avatar,
  Card,
  CopyToClipboard,
  NeutralTag,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { truncateMiddle } from "@certn/utils";
import { formatDate } from "@certn/utils/src/date-utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { ActionsDropdown } from "./ActionsDropdown";
import { TagList } from "../../../components/tag-list/TagList";

interface Props {
  id: string;
  isActive: boolean;
  packageName: string;
  customerGroup: string;
  applyLinkName: string;
  requestorName: string;
  tags: readonly { id: string; name: string }[];
  link: string;
  usage: number;
  createdDate: string;
  mutate: () => void;
}
export function TableDataCard(props: Props) {
  const { t } = useTranslation();
  const {
    id,
    isActive,
    packageName,
    customerGroup,
    applyLinkName,
    requestorName,
    tags,
    link,
    usage,
    createdDate,
    mutate,
  } = props;
  return (
    <Card type="large">
      <div className={styles.container}>
        <div className={styles.fieldContainer}>
          <div className={styles.applyLinkDetails}>
            <Text style="global/heading-small">{applyLinkName}</Text>
            <Text style="global/info-label">
              {packageName} • {customerGroup}
            </Text>
          </div>
          <div className={styles.avatarGroup}>
            <Avatar name={requestorName} />
            <ActionsDropdown
              isActive={isActive}
              link={link}
              id={id}
              mutate={mutate}
            />
          </div>
        </div>
        {!!tags?.length && (
          <TagList
            tags={tags.map((tag) => tag.name)}
            capacity={3}
            testID="apply-link-tags"
          />
        )}
        <div className={styles.fieldContainer}>
          <div className={styles.field}>
            <Text style="global/label-small">{t("Apply link")}</Text>
            <CopyToClipboard
              text={truncateMiddle(link)}
              url={link}
              testID="copy-apply-link"
            />
          </div>
          <div className={styles.field}>
            <Text style="global/label-small">{t("Usage")}</Text>
            <Text style="form/body-secondary">{usage}</Text>
          </div>
        </div>
        <div className={styles.fieldContainer}>
          <div className={styles.field}>
            <Text style="global/label-small">{t("Status")}</Text>
            {isActive ? (
              <SuccessTag testID="active-tag" type="subtle">
                {t("Active")}
              </SuccessTag>
            ) : (
              <NeutralTag type="subtle" testID="deactivated-tag">
                {t("Deactivated")}
              </NeutralTag>
            )}
          </div>
          <div className={styles.field}>
            <Text style="global/label-small">{t("Created date")}</Text>
            <Text style="form/body-secondary">
              {formatDate(createdDate, "MMM d, yyyy")}
            </Text>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = {
  container: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    borderRadius: "var(--border-radius-3)",
    cursor: "pointer",
  }),
  fieldContainer: css({
    display: "flex",
    justifyContent: "space-between",
    gap: "var(--space-3)",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  }),
  field: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    width: "50%",
  }),
  applyLinkDetails: css({
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  }),
  avatarGroup: css({ display: "flex", gap: "var(--space-2)" }),
};
